// External
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import _ from "lodash";
import classNames from "classnames";

// Internal imports
import { Button, Modal } from "@toddle-edu/ds-web";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { AssessmentDetailsComponent } from "Assessments";
import {
  CREATE_WORKSHEET,
  WORKSHEET_QUESTION_CREATION,
} from "Constants/stringConstants";
import { deleteClonedAssessment } from "Assessments/modules/AssessmentModule";
import {
  GENERATE_USING_AI,
  RESOURCE_TYPE_FOLDER,
  SECTION_AND_NODE_KEY,
  ORG_RESOURCE_TYPE_WORKSHEET,
} from "Worksheet/modules/WorksheetConstants";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import {
  createQuestionV2,
  createParentResource,
  updateWorksheetSidebar,
} from "Worksheet/modules/WorksheetModules";
import {
  isWorksheetRevampV3,
  createTypeSessionStorage,
} from "Worksheet/modules/WorksheetUtils";
import {
  getIsWorksheetAIEnabled,
  isWorksheetQueriesV2Enabled,
  isWorksheetMutationsV2Enabled,
} from "Worksheet/modules/WorksheetFeatureFlags";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import WorksheetCreateType from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/components/IBcomponents/WorksheetCreateType/WorksheetCreateType";

// Relative
import classes from "./CreateAssignmentModal.scss";

/**
 *
 * @param {*} t
 * @param {*} assessmentType
 * @returns Get Static Modal Information based on assessmentType
 */
const getModalInfo = (t, assessmentType, { isWorksheetAIRevamp }) => {
  if (assessmentType === "WORKSHEET") {
    return {
      isWorksheet: true,
      mode: "create",
      creationMode: CREATE_WORKSHEET,
      headerTitle: isWorksheetAIRevamp
        ? t("worksheet:create_worksheet_type_prompt")
        : t("classMaterials:create_a_worksheet"),
      submitBtnTitle: t("worksheet:start_creating_worksheet"),
      bodyTopComponent: (
        <div className="text-label">{t("common:basic_details")}</div>
      ),
    };
  }
  return {};
};

/**
 *
 * @param {*} props
 * @returns Assessment Initializing Modal
 */
const CreateAssignmentModal = props => {
  const {
    assessmentId,
    onClose = () => {},
    onSubmit = () => {},
    course = {},
    curriculumType = "",
    learningCourseId,
    portalType = "",
    creationSource = "",
    width = "1000px",
    deleteClonedAssessment,
    assessmentType = "WORKSHEET",
    updateWorksheetSidebar,
    shouldDeleteAssessmentOnClose = true,
    createParentResource,
    createQuestionV2,
  } = props;

  const isWorksheetAIRevamp = isWorksheetRevampV3();
  const isWorksheetAIEnabled = getIsWorksheetAIEnabled();

  const { t } = useTranslation();
  const [isAssessmentLoading, setIsAssessmentLoading] = useState(false);
  const [createType, setCreateType] = useState(GENERATE_USING_AI);

  const {
    mode,
    isWorksheet,
    creationMode,
    headerTitle,
    submitBtnTitle,
    bodyTopComponent,
  } = useMemo(() => getModalInfo(t, assessmentType, { isWorksheetAIRevamp }), [
    t,
    assessmentType,
    isWorksheetAIRevamp,
  ]);

  const [isCalled, setIsCalled] = useState(false);
  const assessmentDetailsRef = useRef(null);

  const setAssessmentDetailsRef = useCallback(ref => {
    assessmentDetailsRef.current = ref;
  }, []);

  const handleClose = () => {
    if (shouldDeleteAssessmentOnClose) {
      deleteClonedAssessment({ assessmentId });
    }

    if (isWorksheet) {
      createTypeSessionStorage("REMOVE");
    }

    onClose?.(false);
  };

  const handleSubmit = () => {
    const isMutationsV2Enabled = isWorksheetMutationsV2Enabled();

    const errorCount = assessmentDetailsRef?.current?.step?.middlePane?.isValid();

    // * If there are no errors, call the onSubmit function
    if (errorCount === 0 || isWorksheetAIRevamp || !isWorksheetAIEnabled) {
      if (isWorksheet) {
        setIsCalled(true);

        // * Waiting for debounce mutation to finish
        setTimeout(async () => {
          // * If worksheet, Setting starting create type as default active
          const assessmentDetails = getAssessmentDetailsFromCache({
            id: assessmentId,
          });
          const allFields = _.get(assessmentDetails, "allFields", []);
          const createTypeField = _.find(allFields, { uid: "createType" });
          const createType = _.get(createTypeField, "value", GENERATE_USING_AI);
          updateWorksheetSidebar({
            defaultActive: isWorksheetAIEnabled
              ? createType || GENERATE_USING_AI
              : WORKSHEET_QUESTION_CREATION,
          });

          // * Create organization resources on worksheet initialization to prevent duplicate resources
          if (isMutationsV2Enabled) {
            await createQuestionV2({
              parentId: assessmentId,
              parentType: "ASSESSMENT",
              questionTypeId: SECTION_AND_NODE_KEY,
              isQueriesV2Enabled: isWorksheetQueriesV2Enabled(),
              setQuestionAsActive: false,
              onlyCheckParentEntities: true,
            });
          } else {
            await createParentResource({
              parentId: assessmentId,
              parentType: "ASSESSMENT",
              organizationResources: [
                {
                  type: ORG_RESOURCE_TYPE_WORKSHEET,
                  resourceType: RESOURCE_TYPE_FOLDER,
                },
              ],
            });
          }

          onSubmit();
          setIsCalled(false);
        }, 1000);
      } else {
        onSubmit();
      }
    }
  };

  useEffect(() => {
    if (!isWorksheetAIEnabled) {
      handleSubmit();
    }
  }, []);

  if (isCalled || !isWorksheetAIEnabled)
    return <FullScreenLoader pContainer={classes.overlayContainer} />;

  return (
    <Modal
      isOpen={true}
      dsVersion="2.0"
      width={isWorksheetAIRevamp ? "max-content" : width}
      height="fit-content"
      onClose={handleClose}
    >
      <Modal.Header hasSeparator={!isWorksheetAIRevamp} title={headerTitle} />

      <Modal.Body isScrollable style={{ minHeight: "auto" }}>
        {isWorksheetAIRevamp ? (
          <WorksheetCreateType
            value={createType}
            fieldKey={"createType"}
            updateInputField={data => {
              const _createType = _.get(data, "createType");
              if (_createType) setCreateType(_createType);
            }}
            testId="worksheet-create-type"
            curriculumType={curriculumType}
            isWorksheetAIRevamp={isWorksheetAIRevamp}
          />
        ) : (
          <div
            className={classNames(classes.bodyContainer, {
              [classes.bodyContainerLoading]: isAssessmentLoading,
            })}
          >
            {!isAssessmentLoading && bodyTopComponent}
            <AssessmentDetailsComponent
              mode={mode}
              isDraft={false}
              assessmentMode={mode}
              isFromUnitPlan={false}
              portalType={portalType}
              renderAsComponent={true}
              creationMode={creationMode}
              sourceFrom={creationSource}
              assessmentId={assessmentId}
              courseGrades={_.get(course, "grades", [])}
              courseSubjects={_.get(course, "subjects", [])}
              selectedCourseId={_.get(course, "id")}
              isFromInitializingModal={true}
              setIsAssessmentLoading={setIsAssessmentLoading}
              setAssessmentDetailsRef={setAssessmentDetailsRef}
              learningCourseId={learningCourseId}
              skipAddAssignmentRender={true}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer hasSeparator={true}>
        <div className={classes.footer}>
          <Button
            size="medium"
            variant="neutral"
            type="outlined"
            onClick={handleClose}
            dsVersion="2.0"
            disabled={isAssessmentLoading}
            testId="assessmentDetailsEdit-cancelCreation"
          >
            {t("common:cancel")}
          </Button>
          <Button
            size="medium"
            variant="primary"
            onClick={handleSubmit}
            disabled={isAssessmentLoading}
            type="fill"
            dsVersion="2.0"
            shortcut={{
              code: "Enter",
              shiftKey: true,
            }}
            testId="assessmentDetailsEdit-startCreation"
          >
            {submitBtnTitle}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = state => {
  const academicYearId = getSelectedAcademicYearId(state);
  const courseGradeFilters = { academicYearId };
  const portalType = _.get(state, "login.userInfo.portalType");
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");

  const learningCourseId = getSelectedLearningCourseId(state);

  return {
    portalType,
    curriculumType,
    learningCourseId,
    courseGradeFilters,
  };
};

const mapDispatchToProps = {
  deleteClonedAssessment,
  updateWorksheetSidebar,
  createParentResource,
  createQuestionV2,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreateAssignmentModal);
